import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';

const ISSUE_TYPES = [
  { value: 'bug', label: 'Bug / Error' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'ui', label: 'UI / UX Feedback' },
  { value: 'other', label: 'Other' }
];

export default function FeedbackDialog({ open, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    type: 'bug',
    summary: '',
    details: ''
  });
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setFormData({ type: 'bug', summary: '', details: '' });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.summary.trim()) {
      nextErrors.summary = 'Please provide a short summary';
    }
    if (!formData.details.trim()) {
      nextErrors.details = 'Details help us reproduce or plan work';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit?.({
      ...formData,
      summary: formData.summary.trim(),
      details: formData.details.trim(),
      createdAt: new Date().toISOString()
    });
    resetForm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Feedback</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Feedback Type"
            value={formData.type}
            onChange={handleChange('type')}
            fullWidth
          >
            {ISSUE_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Summary"
            value={formData.summary}
            onChange={handleChange('summary')}
            fullWidth
            error={Boolean(errors.summary)}
            helperText={errors.summary || 'One sentence overview of the issue, feature, or idea.'}
          />
          <TextField
            label="Details"
            value={formData.details}
            onChange={handleChange('details')}
            fullWidth
            multiline
            minRows={4}
            error={Boolean(errors.details)}
            helperText={errors.details || 'Include reproduction steps, urgency, or desired outcome.'}
          />
          <Typography variant="caption" color="text.secondary">
            Feedback is shared internally with the product team. Please avoid sensitive data.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
